import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { MemberRole, PatientSummary } from '@fatigue/shared';
import { TopBar } from './components/TopBar';
import { NavSidebar } from './components/NavSidebar';
import { PatientSidebar } from './components/PatientSidebar';
import { PatientPanel } from './components/PatientPanel';
import { Administration } from './components/admin/Administration';
import { SignIn } from './components/SignIn';
import { Register } from './components/public/Register';
import { Verify } from './components/public/Verify';
import { Accept } from './components/public/Accept';
import { Welcome } from './components/public/Welcome';
import { listPatients } from './data/api';
import { session } from './data/session';
import { pathOf, viewAt, type View } from './routes';
import { Settings } from './components/Settings';
import './styles/layout.css';

/**
 * Layout shell: top bar over the app's menu, then whichever view is open --
 * for the caseload, the patient list and a main panel.
 *
 * Selection state lives here on purpose. The chart, the comment list and the
 * detail panel are three views of ONE selected entry -- clicking a point
 * selects the comment and clicking a comment selects the point. Keeping the id
 * at this level makes that true by construction rather than by two components
 * trying to stay in sync.
 *
 * There are two views. **Caseload**, at `/`, is the dashboard and where
 * everybody lands, owner included. **Administration**, at `/admin`, is the
 * organisation, its people and cohorts; it is offered only to an owner and
 * contains no patient data at all -- see `components/admin/`. An owner's
 * caseload is empty until they assign themselves to a cohort, and the sidebar
 * says why rather than looking broken.
 *
 * Nothing below renders without a session. `Dashboard` is a separate component
 * rather than an early return inside this one so that its hooks -- the roster
 * fetch in particular -- cannot run for somebody who is not signed in.
 */
/** Remembered so a clinician who works with the list collapsed stays that way. */
const SIDEBAR_KEY = 'fatigue.sidebarCollapsed';
/** Likewise for the menu: open unless somebody has chosen otherwise. */
const NAV_KEY = 'fatigue.navHidden';


/**
 * The pages emailed links and the registration form land on. They render
 * whether or not somebody is signed in: an owner verifying in a browser where
 * a colleague is still signed in has still been sent that link.
 *
 * Paths rather than a router: there are five of them and nothing else in the
 * app is addressable. Cloudflare Pages serves index.html for any path it has
 * no file for, which is what makes these deep links work.
 */
const PUBLIC_PAGES: Readonly<Record<string, () => JSX.Element>> = {
  '/register': Register,
  '/verify': Verify,
  '/accept': Accept,
  '/welcome': () => <Welcome />,
  '/reset': () => <Welcome mode="reset" />,
};

export function App(): JSX.Element {
  const Page = PUBLIC_PAGES[window.location.pathname.replace(/\/+$/, '')];
  if (Page !== undefined) return <Page />;

  const signedIn = session();
  return signedIn === null ? <SignIn /> : <Dashboard role={signedIn.role} />;
}

function Dashboard({ role }: { readonly role: MemberRole }): JSX.Element {
  const isOwner = role === 'owner';

  const [collapsed, setCollapsed] = useState<boolean>(
    () => window.localStorage.getItem(SIDEBAR_KEY) === 'true',
  );
  const [navOpen, setNavOpen] = useState<boolean>(
    () => window.localStorage.getItem(NAV_KEY) !== 'true',
  );
  // Everybody lands on the caseload; administration is at its own address,
  // and only an owner is shown it. Anyone else asking for it gets the
  // caseload, and the address is corrected to match.
  const [view, setView] = useState<View>(() => viewAt(window.location.pathname, isOwner));

  useEffect(() => {
    // Correct an address that asked for something this person cannot see.
    if (window.location.pathname !== pathOf(view)) {
      window.history.replaceState(null, '', pathOf(view));
    }
    const onPop = (): void => setView(viewAt(window.location.pathname, isOwner));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // Once, on mount: after that the address only changes through changeView
    // or the browser's own back and forward.
  }, []);

  const changeView = (next: View): void => {
    if (next === view) return;
    window.history.pushState(null, '', pathOf(next));
    setView(next);
  };
  const [patients, setPatients] = useState<readonly PatientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientId, setPatientId] = useState<string | null>(null);
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    listPatients()
      .then((loaded) => {
        if (cancelled) return;
        setPatients(loaded);
        // Open on the first patient rather than an empty panel. A clinician
        // arriving here is going to pick someone; showing a record they can
        // read, or switch away from in one click, beats asking first.
        setPatientId((current) => current ?? loaded[0]?.patientId ?? null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not reach the API.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const choosePatient = (id: string): void => {
    setPatientId(id);
    // A pinned entry belongs to the patient it came from.
    setSelectedEntryId(null);
  };

  const patient = patients.find((p) => p.patientId === patientId) ?? null;

  const toggleSidebar = (): void => {
    setCollapsed((current) => {
      window.localStorage.setItem(SIDEBAR_KEY, String(!current));
      return !current;
    });
  };

  const toggleNav = (): void => {
    setNavOpen((current) => {
      window.localStorage.setItem(NAV_KEY, String(current));
      return !current;
    });
  };

  return (
    <div className="app">
      <TopBar navOpen={navOpen} onToggleNav={toggleNav} onChangeView={changeView} />

      <div className="app__body">
        <NavSidebar
          open={navOpen}
          view={view}
          onChangeView={changeView}
          showAdministration={isOwner}
        />

        <div className="app__main">
          {view === 'settings' ? (
            <div className="app__single">
              <Settings />
            </div>
          ) : view === 'administration' ? (
            <div className="app__single">
              <Administration />
            </div>
          ) : (
            <div className={`app__cols${collapsed ? ' app__cols--rail' : ''}`}>
              <PatientSidebar
                collapsed={collapsed}
                onToggle={toggleSidebar}
                patients={patients}
                loading={loading}
                error={error}
                selectedPatientId={patientId}
                onSelect={choosePatient}
                emptyReason={emptyReason(isOwner)}
              />
              <PatientPanel
                patient={patient}
                /* With the first patient selected on arrival, an empty panel now
                   means the roster itself is empty, not that nobody has clicked. */
                emptyMessage={loading || error !== null ? null : emptyReason(isOwner)}
                selectedEntryId={selectedEntryId}
                onSelectEntry={setSelectedEntryId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Why the caseload is empty.
 *
 * An owner's empty caseload is the design working -- administering is not
 * reading -- so it gets an explanation and the fix, rather than the blank
 * "no patients yet" that would read as a fault.
 */
function emptyReason(isOwner: boolean): string {
  return isOwner
    ? 'You are not assigned to a cohort, so you have no caseload. Assign yourself to one in Administration to see patients.'
    : 'No patients in your cohorts yet. Invite one, or ask the account owner to assign you to a cohort.';
}
